import { useEffect, useMemo, useRef, useState } from 'react';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useAuthStore } from '../store/useAuthStore';
import { fetchUserBranches } from '../application/authBranches';
import { getLastSelectedBranchId, setLastSelectedBranchId } from '../../shared/localPrefs';
import { listBranchesWithOpenCashSession } from '../../cash/infra-api';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useIsCurrentUserEmployee } from '../hooks/useIsCurrentUserEmployee';
import { useConnectivityStatus } from '../../shell/connectivity/useConnectivityStatus';
import { dismissSelfThenNavigateRoot, navigateRoot } from '../../shell/navigation/rootNavigation';
import { showError } from '../../shared/ui/appToast'
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { matchesSearchText } from '../../shared/format/searchText';

export function useBranchSelectionScreen(navigation: NavigationProp<ParamListBase>) {
  const { branches, currentBranchId, setCurrentBranch, setBranches, branchesLoading } = useSessionStore();
  const session = useAuthStore((state) => state.session);
  const { entitlements, refresh: refreshEntitlements } = usePlanEntitlements();
  const isEmployeeRow = useIsCurrentUserEmployee();
  const isOwner = entitlements?.is_owner === true;
  const hasOrganization = Boolean(entitlements?.organization_id);
  const connectivityStatus = useConnectivityStatus();

  // Selector = solo activas (bootstrap viejo podía meter inactivas sin flag).
  const activeBranches = useMemo(
    () => branches.filter((b) => b.isActive !== false),
    [branches],
  );

  const [lastBranchId, setLastBranchId] = useState<string | null>(null);
  const [cashSessionByBranch, setCashSessionByBranch] = useState<Record<string, boolean>>({});
  const [offlineSwitchBranchId, setOfflineSwitchBranchId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const openedAsBranchChangeModalRef = useRef(!!currentBranchId);

  const sortedBranches = useMemo(() => {
    const list = [...activeBranches];
    if (lastBranchId && list.some((b) => b.id === lastBranchId)) {
      const last = list.find((b) => b.id === lastBranchId);
      const rest = list.filter((b) => b.id !== lastBranchId).sort((a, b) => a.name.localeCompare(b.name));
      return last ? [last, ...rest] : list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [activeBranches, lastBranchId]);

  // Preparado para escalar (10+ sucursales): solo tiene efecto cuando el input de
  // búsqueda está visible (ver BranchSelectionScreen, umbral por branchCount).
  const filteredBranches = useMemo(() => {
    if (!searchQuery.trim()) return sortedBranches;
    return sortedBranches.filter(
      (branch) => matchesSearchText(branch.name, searchQuery) || matchesSearchText(branch.location, searchQuery),
    );
  }, [sortedBranches, searchQuery]);

  const branchesForList = useMemo(
    () => filteredBranches.filter((branch) => branch.id !== currentBranchId),
    [filteredBranches, currentBranchId],
  );

  const hasOtherBranches = useMemo(
    () => sortedBranches.some((branch) => branch.id !== currentBranchId),
    [sortedBranches, currentBranchId],
  );

  const shouldRenderBranchList =
    branchesLoading || sortedBranches.length === 0 || !currentBranchId || hasOtherBranches;

  const currentBranch = useMemo(
    () => sortedBranches.find((branch) => branch.id === currentBranchId),
    [sortedBranches, currentBranchId],
  );

  const emptyStateKind: 'owner' | 'employee' = isEmployeeRow === true && !isOwner ? 'employee' : 'owner';

  const applyBranchSelection = async (branchId: string) => {
    const switchingFromAnotherBranch = !!currentBranchId && currentBranchId !== branchId;
    setCurrentBranch(branchId);
    await setLastSelectedBranchId(branchId);
    if (switchingFromAnotherBranch && navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const handleSelect = (branchId: string) => {
    const branch = activeBranches.find((b) => b.id === branchId);
    if (!branch) {
      showError('Error', 'No encontramos esa sucursal.');
      return;
    }
    if (!connectivityStatus.isOnline) {
      setOfflineSwitchBranchId(branchId);
      return;
    }
    void applyBranchSelection(branchId);
  };

  const handleCreateBranch = () => {
    if (!session?.user?.id) {
      showError('Sesión inválida', 'Volvé a iniciar sesión para crear una sucursal.');
      return;
    }
    if (isEmployeeRow === true && !isOwner) {
      showError('Sin permiso', 'Solo el dueño de la cuenta puede crear sucursales.');
      return;
    }
    if (openedAsBranchChangeModalRef.current) {
      dismissSelfThenNavigateRoot(navigation, 'CreateBranch');
      return;
    }
    navigation.navigate('CreateBranch' as never);
  };

  const canCreateBranch = isOwner;
  const canManageBranches = isOwner;

  const handleManageBranches = () => {
    if (!canManageBranches) return;
    if (openedAsBranchChangeModalRef.current) {
      dismissSelfThenNavigateRoot(navigation, 'ManageBranchesOverlay');
      return;
    }
    navigateRoot(navigation, 'ManageBranchesOverlay');
  };

  // #1296: una sola RPC batch en vez de N getMergedActiveCashSessionId.
  // null = desconocido → se conservan los badges previos.
  const refreshCashSessionBadges = async (branchList: typeof branches) => {
    const open = await listBranchesWithOpenCashSession(branchList.map((branch) => branch.id));
    if (open == null) return;
    setCashSessionByBranch(
      Object.fromEntries(branchList.map((branch) => [branch.id, open.has(branch.id)])),
    );
  };

  const { refreshing, onRefresh } = useScreenRefresh(async () => {
    const last = await getLastSelectedBranchId();
    setLastBranchId(last);
    try {
      const [nextBranches] = await Promise.all([
        fetchUserBranches({ force: true }),
        refreshEntitlements({ force: true }),
      ]);
      setBranches(nextBranches);
      await refreshCashSessionBadges(nextBranches);
    } catch {
      await refreshCashSessionBadges(activeBranches);
    }
  });

  useEffect(() => {
    getLastSelectedBranchId().then(setLastBranchId);
  }, []);

  useEffect(() => {
    if (activeBranches.length === 0) return;
    void refreshCashSessionBadges(activeBranches);
  }, [activeBranches]);

  useEffect(() => {
    if (!currentBranchId || openedAsBranchChangeModalRef.current) return;
    const timeout = setTimeout(() => navigation.navigate('AppShell' as never), 0);
    return () => clearTimeout(timeout);
  }, [currentBranchId, navigation]);

  useEffect(() => {
    if (activeBranches.length === 1 && !currentBranchId) {
      const branchId = activeBranches[0].id;
      setCurrentBranch(branchId);
      void setLastSelectedBranchId(branchId);
    }
  }, [activeBranches, currentBranchId, setCurrentBranch]);

  const confirmOfflineSwitch = () => {
    const branchId = offlineSwitchBranchId;
    setOfflineSwitchBranchId(null);
    if (branchId) void applyBranchSelection(branchId);
  };

  return {
    hasOrganization,
    sortedBranches,
    filteredBranches,
    branchesForList,
    shouldRenderBranchList,
    currentBranch,
    currentBranchId,
    branchesLoading,
    cashSessionByBranch,
    refreshing,
    emptyStateKind,
    offlineSwitchBranchId,
    searchQuery,
    setSearchQuery,
    setOfflineSwitchBranchId,
    confirmOfflineSwitch,
    onRefresh,
    handleSelect,
    handleCreateBranch,
    handleManageBranches,
    canCreateBranch,
    canManageBranches,
  };
}
